import { useCallback } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { getUser } from '@frontend/auth-helpers';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { useInboxRobocallTagNotification } from '@frontend/notifications';
import { useAppScopeStore } from '@frontend/scope';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * A hook that returns a function to execute when a robocall tag event is received.
 * This doesn't have any WebSocket subscription logic, just the handler. Useful for testing.
 */
export const useInboxRobocallTagNotificationWSHandler = () => {
  const { navigateToThread } = useInboxNavigate();
  const userId = getUser()?.userID;
  const { selectedLocationIds } = useAppScopeStore();
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);
  const { create } = useInboxRobocallTagNotification({
    onView: (event) => {
      navigateToThread({
        threadId: event.payload.threadId,
        groupId: event.payload.locationId,
        personId: event.payload.personId,
        threadItemId: event.payload.channelId,
        threadItemCreatedAt: event.payload.startedAt,
      });
    },
  });

  const eventHandler = useCallback<GetWebsocketEventHandler<NotificationType.ROBOCALL_TAG>>(
    (event) => {
      const {
        params: { action, userId: taggedByUserId, weaveNumber },
      } = event;

      if (taggedByUserId === userId) {
        // Ignore events triggered by the current user
        return;
      }

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!weaveNumber && !checkUserLocationPhoneAccess(weaveNumber)) {
        return;
      }

      switch (action) {
        case 'APPLIED':
          create({
            id: String(event.id),
            type: 'inbox-robocall-tag',
            payload: event.params,
            timestamp: Date.now(),
            state: {
              paused: false,
              status: 'unread',
            },
          });
      }
    },
    [create]
  );

  return eventHandler;
};

export const useInboxRobocallTagNotificationSubscription = () => {
  const handler = useInboxRobocallTagNotificationWSHandler();
  return useWebsocketEventSubscription(NotificationType.ROBOCALL_TAG, handler);
};
